import { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
const COLORS = ['#047857','#10b981','#34d399','#6ee7b7','#f59e0b'];

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function ReportsPage() {
  const { isAdmin } = useAuth();
  const [period, setPeriod]     = useState(getCurrentPeriod());
  const [results, setResults]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [locking, setLocking]   = useState(false);
  const [paying, setPaying]     = useState(false);
  const toast = useToast();

  useEffect(() => { loadData(); }, [period]);

  async function loadData() {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/commission?period=${period}`),
        api.get(`/reports/summary?period=${period}`),
      ]);
      setResults(r);
      setSummary(s);
    } catch {} finally { setLoading(false); }
  }

  async function lockAll() {
    setLocking(true);
    try {
      const res = await api.post('/reports/lock-all', { period });
      toast.success(`تم قفل ${res.locked} نتيجة`);
      await loadData();
    } catch (err) { toast.error(err.message); } finally { setLocking(false); }
  }

  async function payAll() {
    setPaying(true);
    try {
      const res = await api.post('/reports/pay-all', { period });
      toast.success(`تم تحديد ${res.paid} نتيجة كمدفوعة`);
      await loadData();
    } catch (err) { toast.error(err.message); } finally { setPaying(false); }
  }

  async function exportCSV() {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/reports/export?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission_${period}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
  }

  const periods = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    periods.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const topResults = [...results]
    .sort((a,b) => parseFloat(b.total_earned) - parseFloat(a.total_earned))
    .slice(0, 8);

  const barData = topResults.map(r => ({
    name: (r.name_ar || r.username || '').split(' ')[0],
    'عمولة': parseFloat(parseFloat(r.total_earned || 0).toFixed(0)),
  }));

  const pieData = summary ? [
    { name: 'مدفوع',  value: parseFloat(summary.paid_commission   || 0) },
    { name: 'مقفل',   value: parseFloat(summary.total_commission  || 0) - parseFloat(summary.paid_commission || 0) },
  ].filter(d => d.value > 0) : [];

  const kpiBreakdown = results.length > 0 ? [
    { name: 'كمية',       value: results.reduce((s,r) => s + parseFloat(r.qty_earned||0), 0) },
    { name: 'مرتجع رديء', value: results.reduce((s,r) => s + parseFloat(r.bad_return_earned||0), 0) },
    { name: 'تغطية',      value: results.reduce((s,r) => s + parseFloat(r.coverage_earned||0), 0) },
    { name: 'تحصيل',      value: results.reduce((s,r) => s + parseFloat(r.collection_earned||0), 0) },
    { name: 'هدف خاص',    value: results.reduce((s,r) => s + parseFloat(r.specific_earned||0), 0) },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="page">
      <Header title="التقارير" />
      <div className="page-content">

        {/* Period Selector */}
        <div style={styles.periodScroll}>
          {periods.map(p => (
            <button key={p} style={{
              ...styles.periodBtn,
              background: p === period ? 'var(--primary)' : 'white',
              color:      p === period ? 'white' : 'var(--gray-600)',
              border:     `1.5px solid ${p === period ? 'var(--primary)' : 'var(--gray-200)'}`,
            }} onClick={() => setPeriod(p)}>
              {MONTHS_AR[parseInt(p.split('-')[1]) - 1]} {p.split('-')[0]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-page"><div className="spinner" /><span>جاري التحميل...</span></div>
        ) : results.length === 0 ? (
          <div className="empty-state">
            <h3>لا توجد نتائج لهذه الفترة</h3>
            <p style={{ fontSize: 13 }}>قم باحتساب العمولات أولاً من صفحة الإدارة</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div style={styles.grid2}>
              <div style={styles.summaryCard}>
                <p style={styles.summaryLabel}>إجمالي العمولات</p>
                <p style={styles.summaryValue}>{parseFloat(summary?.total_commission || 0).toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س</p>
              </div>
              <div style={styles.summaryCard}>
                <p style={styles.summaryLabel}>عدد الموظفين</p>
                <p style={styles.summaryValue}>{summary?.total_employees || 0}</p>
              </div>
              <div style={styles.summaryCard}>
                <p style={styles.summaryLabel}>متوسط العمولة</p>
                <p style={styles.summaryValue}>{parseFloat(summary?.avg_commission || 0).toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س</p>
              </div>
              <div style={styles.summaryCard}>
                <p style={styles.summaryLabel}>مدفوع / إجمالي</p>
                <p style={styles.summaryValue}>{summary?.paid_count || 0} / {summary?.total_employees || 0}</p>
              </div>
            </div>

            {/* Action Buttons */}
            {isAdmin && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button className="btn btn-sm btn-outline" style={{ flex: 1 }} onClick={exportCSV}>
                  📥 تصدير Excel
                </button>
                <button className="btn btn-sm" style={{ flex: 1, background: '#dbeafe', color: '#1e40af' }}
                  onClick={lockAll} disabled={locking || !summary?.live_count}>
                  {locking ? 'جاري...' : `🔒 قفل (${summary?.live_count || 0})`}
                </button>
                <button className="btn btn-sm btn-primary" style={{ flex: 1 }}
                  onClick={payAll} disabled={paying || !summary?.locked_count}>
                  {paying ? 'جاري...' : `✓ دفع (${summary?.locked_count || 0})`}
                </button>
              </div>
            )}

            {/* Bar Chart - Top performers */}
            {barData.length > 0 && (
              <div style={styles.chartCard}>
                <p style={styles.chartTitle}>أعلى الموظفين عمولةً</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={barData} layout="vertical" margin={{ right: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={55} />
                    <Tooltip formatter={v => v.toLocaleString('ar') + ' ر.س'} />
                    <Bar dataKey="عمولة" fill="var(--primary)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* KPI Breakdown Pie */}
            {kpiBreakdown.length > 0 && (
              <div style={styles.chartCard}>
                <p style={styles.chartTitle}>توزيع العمولات حسب المؤشر</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ResponsiveContainer width={130} height={130}>
                    <PieChart>
                      <Pie data={kpiBreakdown} cx="50%" cy="50%" outerRadius={55} dataKey="value">
                        {kpiBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1 }}>
                    {kpiBreakdown.map((d, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                        <span style={{ fontSize: 12, flex: 1 }}>{d.name}</span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{d.value.toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Status Distribution */}
            <div style={styles.chartCard}>
              <p style={styles.chartTitle}>حالة العمولات</p>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { label: 'جاري',   count: summary?.live_count   || 0, color: 'var(--gray-100)',  text: 'var(--gray-600)' },
                  { label: 'مقفل',   count: summary?.locked_count || 0, color: '#dbeafe',           text: '#1e40af' },
                  { label: 'مدفوع',  count: summary?.paid_count   || 0, color: 'var(--green-100)', text: 'var(--green-700)' },
                ].map(s => (
                  <div key={s.label} style={{ flex: 1, background: s.color, borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: s.text }}>{s.count}</div>
                    <div style={{ fontSize: 12, color: s.text, opacity: 0.8 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Full Results Table */}
            <div style={styles.chartCard}>
              <p style={styles.chartTitle}>تفاصيل العمولات</p>
              {results.sort((a,b) => parseFloat(b.total_earned) - parseFloat(a.total_earned)).map(r => (
                <div key={r.user_id} style={styles.resultRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{r.name_ar}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{r.branch_name_en}</div>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--primary)' }}>
                      {parseFloat(r.total_earned || 0).toLocaleString('ar', { maximumFractionDigits: 0 })} ر.س
                    </div>
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 600,
                      background: r.status === 'paid' ? 'var(--green-100)' : r.status === 'locked' ? '#dbeafe' : 'var(--gray-100)',
                      color:      r.status === 'paid' ? 'var(--green-700)' : r.status === 'locked' ? '#1e40af' : 'var(--gray-500)',
                    }}>
                      {r.status === 'paid' ? 'مدفوع' : r.status === 'locked' ? 'مقفل' : 'جاري'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  periodScroll: {
    display: 'flex', gap: 8, overflowX: 'auto',
    paddingBottom: 12, marginBottom: 12, scrollbarWidth: 'none',
  },
  periodBtn: {
    padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font-main)', transition: 'all 0.2s',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 },
  summaryCard: {
    background: 'white', borderRadius: 14, padding: '14px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
  },
  summaryLabel: { fontSize: 12, color: 'var(--gray-400)', marginBottom: 4 },
  summaryValue: { fontSize: 18, fontWeight: 800, color: 'var(--primary)' },
  chartCard: {
    background: 'white', borderRadius: 14, padding: 16,
    marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
  },
  chartTitle: { fontSize: 14, fontWeight: 700, marginBottom: 12 },
  resultRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 0', borderBottom: '1px solid var(--gray-100)',
  },
};
